/* ==========================================================================
   motion.js — scroll orchestration.

   IntersectionObserver handles every reveal (cheap, no frame cost at rest).
   GSAP ScrollTrigger is reserved for the two things it is actually needed
   for: the pinned services track and the scrubbed process spine.
   ========================================================================== */

(function () {
  'use strict';

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var gsap = window.gsap;
  var ST = window.ScrollTrigger;

  if (gsap && ST) gsap.registerPlugin(ST);

  /* --- Scrolling -----------------------------------------------------------
     Native. A JS smooth-scroll library drives scrollTop from a main-thread rAF
     loop after preventDefault-ing the wheel, so every frame the main thread is
     busy is a frame the page refuses to move. Native scroll runs on the
     compositor and stays responsive regardless of what JS is doing. */

  /* --- Reveals ------------------------------------------------------------ */

  var REVEAL = '.reveal, .reveal-mask, .reveal-notch, .reveal-scale, .reveal-wipe, [data-reveal]';

  // Auto-stagger: siblings inside [data-stagger] get a cascading delay so no
  // component has to hand-place one.
  document.querySelectorAll('[data-stagger]').forEach(function (group) {
    Array.prototype.forEach.call(group.children, function (child, i) {
      if (child.style.transitionDelay) return;
      child.style.transitionDelay = Math.min(i * 90, 540) + 'ms';
    });
  });

  // Index custom property for anything driven by --i in CSS.
  document.querySelectorAll('[data-index-children]').forEach(function (group) {
    Array.prototype.forEach.call(group.children, function (child, i) {
      child.style.setProperty('--i', i);
      var inner = child.firstElementChild;
      if (inner) inner.style.setProperty('--i', i);
    });
  });

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-visible');
      io.unobserve(entry.target);
    });
    // threshold 0 plus a lifted bottom margin: fires as soon as the element
    // crosses the trigger line, so blocks taller than the viewport (which can
    // never be 20% visible) still reveal.
  }, { threshold: 0, rootMargin: '0px 0px -12% 0px' });

  document.querySelectorAll(REVEAL).forEach(function (el) {
    if (reduce) { el.classList.add('is-visible'); return; }
    io.observe(el);
  });

  /* --- Counters ----------------------------------------------------------- */

  var counters = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      counters.unobserve(entry.target);
      run(entry.target);
    });
  }, { threshold: 0.6 });

  function run(el) {
    var target = parseInt(el.dataset.countTo, 10) || 0;
    if (reduce) { el.textContent = target; return; }
    var start = performance.now();
    var dur = 900;
    (function step(now) {
      var p = Math.min((now - start) / dur, 1);
      el.textContent = Math.round(target * (1 - Math.pow(1 - p, 3)));
      if (p < 1) requestAnimationFrame(step);
    })(start);
  }

  document.querySelectorAll('[data-count-to]').forEach(function (el) { counters.observe(el); });

  /* --- Hero mask reveal --------------------------------------------------- */

  var hero = document.querySelector('[data-hero-lines]');
  if (hero && !reduce) {
    Array.prototype.forEach.call(hero.children, function (line, i) {
      line.style.setProperty('--i', i);
    });
    requestAnimationFrame(function () { hero.classList.add('is-visible'); });
  } else if (hero) {
    hero.classList.add('is-visible');
  }

  /* --- Services track ------------------------------------------------------
     Native horizontal scroll with snap. No pin, no spacer, so it cannot leave
     a gap in the page when a refresh mis-measures. JS here only mirrors the
     scroll position onto the progress bar. */

  var track = document.querySelector('[data-track] .track');
  var trackBar = document.querySelector('[data-track] .track__progress span');

  if (track && trackBar) {
    track.addEventListener('scroll', function () {
      var max = track.scrollWidth - track.clientWidth;
      trackBar.style.setProperty('--progress', max > 0 ? (track.scrollLeft / max).toFixed(3) : 1);
    }, { passive: true });
  }

  /* --- Header scroll progress --------------------------------------------- */

  var progress = document.querySelector('.header__progress');
  var header = document.querySelector('.header');
  var bar = document.querySelector('[data-emergency-bar]');
  var lastY = window.scrollY;
  var ticking = false;

  function onScroll() {
    var y = window.scrollY;
    var max = document.documentElement.scrollHeight - window.innerHeight;

    if (progress) progress.style.setProperty('--progress', max > 0 ? (y / max).toFixed(4) : 0);
    if (header) header.classList.toggle('is-stuck', y > 80);
    if (bar) bar.classList.toggle('is-hidden', y > lastY && y > 240);

    lastY = y;
    ticking = false;
  }

  // The one legitimate raw scroll listener in this theme: a progress readout,
  // rAF-throttled and passive, touching no layout.
  window.addEventListener('scroll', function () {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(onScroll);
  }, { passive: true });

  onScroll();
})();
